import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { FormikProps } from "formik";
import { memo, useState } from "react";
import { NavigateAction } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { NEW_APPOINTMENT_VISIT_TYPE } from "../../../../constants/scheduling";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import { useDebounce } from "../../../../hooks/use-debounce";
import useDisclosure from "../../../../hooks/use-disclosure";
import EditAvailabilityModal from "../../../../pages/app/provider/scheduling/availability/EditAvailabilityModal";
import { useAppointmentTypeGetAll } from "../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-get-all";
import { useLocationOptionsV2 } from "../../../../queries/use-location-options-v2";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
} from "../../../../utils/utils";
import AsyncSelect from "../../../AsyncSelect";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import Flex from "../../../Flex";
import ProviderSelectScroll from "@/components/ProviderSelectScroll";
import { useRoleController } from "../../../useRoleController";
import CustomTitle from "../../CustomTitle";
import StyledButtonV2 from "../../StyledButtonV2";
import FilterSelect from "../../../FilterSelect";

interface ToolBarAvailabilityProps {
  label: string;
  onNavigate: (navigate: NavigateAction) => void;
  formik: FormikProps<any>;
}

const ToolBarAvailability = memo((props: ToolBarAvailabilityProps) => {
  // const [searchProvider, setSearchProvider] = useDebounce<string>("");
  const [searchAppointmentTypeKey, setSearchAppointmentTypeKey] =
    useDebounce<string>("");
  const [appointmentTypePagination, setAppointmentTypePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [searchLocationKey, setSearchLocationKey] = useDebounce<string>("");
  const { hasUpdate } = useRoleController("Availability Management");

  const { data: providerGroupLocationList } = useLocationOptionsV2({
    searchString: searchLocationKey,
    xTENANTID: getXtenantId(),
    enabled: props?.formik.values?.mode === "IN_PERSON",
  });

  // const { data: providersList } = useProvidersList({
  //   pagination: DEF_PAGINATION,
  //   searchKey: searchProvider,
  //   xTENANTID: localStorage.getItem("realm") ?? "",
  // });

  const {
    data: appointmentTypeList,
    isLoading: appointmentTypeDataLoading,
    isFetching,
  } = useAppointmentTypeGetAll({
    pagination: appointmentTypePagination,
    searchString: searchAppointmentTypeKey,
    status: true,
    archive: false,
    xTENANTID: getXtenantId(),
  });

  const editAvailabilityDisclosure = useDisclosure(false);

  const handleValueChange = (k: any, v: any) => {
    props.formik.setFieldValue(k, v);
  };
  const [me] = useCurrentUser();

  return (
    <Flex justifyContent={"space-between"} alignItems={"center"}>
      <Flex alignItems={"center"} justifyContent={"left"}>
        <CustomTitle>Availability</CustomTitle>
        <IconButton
          sx={{ color: "#1A1A1ACC", marginLeft: "1.5rem" }}
          onClick={() => props.onNavigate("PREV")}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          sx={{
            color: "#1A1A1ACC",
          }}
          onClick={() => props.onNavigate("NEXT")}
        >
          <ChevronRight />
        </IconButton>
        <Typography
          sx={{
            color: "#1A1A1ACC",
            letterSpacing: "-0.17px !important",
            fontSize: "1rem",
            fontWeight: "bold",
          }}
          variant="body2"
        >
          {props.label}
        </Typography>
      </Flex>
      <Flex
        alignItems={"center"}
        justifyContent={"flex-end"}
        gap={2}
        width={"70vw"}
      >
        <Box minWidth={"18%"}>
          <ProviderSelectScroll
            isMatchButtonHeight
            required
            value={props.formik.values?.providers}
            prefillListOption={{
              value: me?.uuid as string,
              label: `${me?.firstName || ""} ${me?.lastName || ""}`.trim(),
            }}
            onChange={(val) => handleValueChange("providers", val)}
            handleBlur={props?.formik.handleBlur}
            error={
              (props?.formik.touched?.providerId &&
                props?.formik.errors?.providerId) as any
            }
          />
          {/* <AsyncSelect
            value={props.formik.values?.providers}
            placeholder="Select Provider"
            onSearch={setSearchProvider}
            options={providersList?.data.content?.map((provider) => {
              return {
                value: provider?.uuid,
                label: provider?.firstName + " " + provider.lastName,
              };
            })}
            // endIcon={<GroupIcon />}
            onChange={(val) => handleValueChange("providers", val)}
          /> */}
        </Box>

        <Box minWidth={"170px"}>
          {/* use custom select if you want radio buttons inside the options */}
          <FilterSelect
            value={props?.formik.values?.mode}
            onChange={(val) => {
              handleValueChange("mode", val);
              if (val === "VIRTUAL") handleValueChange("locations", null);
            }}
            clearable
            placeholder="Visit Mode"
            options={NEW_APPOINTMENT_VISIT_TYPE}
          />
        </Box>

        <Box minWidth={"18%"}>
          <AsyncSelect
            value={props.formik.values?.locations}
            disabled={props?.formik.values?.mode === "VIRTUAL"}
            placeholder="Select Location"
            // endIcon={<LocationOnIcon />}
            onSearch={setSearchLocationKey}
            options={providerGroupLocationList?.data?.map((it) => ({
              value: it.uuid,
              label: it.name,
            }))}
            onChange={(val) => handleValueChange("locations", val)}
            isMatchButtonHeight
          />
        </Box>

        <Box minWidth={"18%"}>
          <AsyncSelectScroll
            loading={appointmentTypeDataLoading || isFetching}
            value={props.formik.values?.type}
            placeholder="Select Appointment Type"
            // endIcon={<LocationOnIcon />}
            onSearch={(val) => {
              setSearchAppointmentTypeKey(val);
              setAppointmentTypePagination((p) => ({ ...p, page: 0 }));
            }}
            options={appointmentTypeList?.data?.content?.map((it: any) => ({
              value: it?.uuid,
              label: it?.nameString,
              duration: it?.duration,
            }))}
            onChange={(val) => handleValueChange("type", val)}
            isMatchButtonHeight
            pagination={appointmentTypePagination}
            setPagination={setAppointmentTypePagination}
            totalElements={appointmentTypeList?.data?.page?.totalElements}
          />
        </Box>

        {/* <Box minWidth={"200px"}>
          <CustomSelect
            value={props?.formik.values?.type}
            onChange={(val) => handleValueChange("type", val)}
            clearable
            // withStatusBox
            placeholder="Appointment Type"
            options={NEW_APPOINTMENT_TYPE}
          />
        </Box> */}
        {/*future reference
         <SquareIcon sx={{ background: "#FCD27F", color: "#FCD27F" }} />
          <Typography>In- Person</Typography>
        </Flex>
        <Flex gap={"8px"}>
          <SquareIcon sx={{ background: "#A5DEFF", color: "#A5DEFF" }} />
          <Typography>Virtual</Typography> */}

        {hasUpdate && (
          <Box>
            <StyledButtonV2
              label={"Edit Availability"}
              onClick={() =>
                setTimeout(() => {
                  editAvailabilityDisclosure.open(
                    props.formik.values?.providers
                  );
                }, 0)
              }
              shouldEditIcon
            />
          </Box>
        )}
        {editAvailabilityDisclosure.opened && (
          <EditAvailabilityModal
            providerIdObj={props?.formik?.values?.providers}
            disclosureHost={editAvailabilityDisclosure}
          />
        )}
      </Flex>
    </Flex>
  );
});

export default ToolBarAvailability;
